import React, { useEffect, useState } from 'react';
import { Card, Steps, Button, Input, Select, Alert, message, Collapse } from 'antd';
import {
  CheckCircleOutlined, 
  CopyOutlined, 
  AmazonOutlined,
  SafetyOutlined,
  DollarOutlined,
  CodeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import './AWSSetup.css';

const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;
const AWSSetup: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [accountId, setAccountId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerDomain, setCustomerDomain] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [externalId, setExternalId] = useState('');
  const [finOpsPrincipalArn, setFinOpsPrincipalArn] = useState('');
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingError, setOnboardingError] = useState('');
  const [region, setRegion] = useState('us-east-1');

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copiado!`);
  };

  const generateRoleArn = () => {
    if (accountId.length === 12) {
      const arn = `arn:aws:iam::${accountId}:role/FinOpsReadOnlyRole`;
      setRoleArn(arn);
      message.success('Role ARN gerado!');
    } else {
      message.error('Account ID deve ter 12 dígitos');
    }
  };

  useEffect(() => {
    if (!roleArn) return;
    let active = true;
    setOnboardingLoading(true);
    setOnboardingError('');

    apiClient.get('/cloud/aws/onboarding', { params: { roleArn } })
      .then(response => {
        if (!active) return;
        setExternalId(response.data.externalId);
        setFinOpsPrincipalArn(response.data.principalArn);
      })
      .catch((error: any) => {
        if (!active) return;
        setExternalId('');
        setFinOpsPrincipalArn('');
        setOnboardingError(error.response?.data?.message || 'Não foi possível gerar a configuração segura.');
      })
      .finally(() => {
        if (active) setOnboardingLoading(false);
      });

    return () => { active = false; };
  }, [roleArn]);

  const trustPolicy = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": "${finOpsPrincipalArn}"
      },
      "Action": "sts:AssumeRole",
      "Condition": {
        "StringEquals": {
          "sts:ExternalId": "${externalId}"
        }
      }
    }
  ]
}`;

  const costExplorerPolicy = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "ce:GetCostAndUsage",
        "ce:GetCostForecast",
        "ce:GetDimensionValues",
        "ce:GetRightsizingRecommendation",
        "ce:GetReservationCoverage",
        "ce:GetReservationUtilization",
        "ce:GetReservationPurchaseRecommendation",
        "ce:GetSavingsPlansCoverage",
        "ce:GetSavingsPlansUtilization",
        "ce:GetSavingsPlansPurchaseRecommendation",
        "compute-optimizer:GetEnrollmentStatus",
        "compute-optimizer:GetEC2InstanceRecommendations",
        "compute-optimizer:GetEBSVolumeRecommendations",
        "compute-optimizer:GetRDSDatabaseRecommendations",
        "compute-optimizer:GetLambdaFunctionRecommendations",
        "compute-optimizer:GetECSServiceRecommendations"
      ],
      "Resource": "*"
    }
  ]
}`;

  const cliCommands = {
    createRole: `aws iam create-role \\
  --role-name FinOpsReadOnlyRole \\
  --assume-role-policy-document file://trust-policy.json \\
  --description "Role read-only para FinOps Dashboard"`,
    
    attachReadOnly: `aws iam attach-role-policy \\
  --role-name FinOpsReadOnlyRole \\
  --policy-arn arn:aws:iam::aws:policy/ReadOnlyAccess`,
    
    createCostPolicy: `aws iam create-policy \\
  --policy-name FinOpsCostExplorerReadOnly \\
  --policy-document file://cost-explorer-policy.json`,
    
    attachCostPolicy: `aws iam attach-role-policy \\
  --role-name FinOpsReadOnlyRole \\
  --policy-arn arn:aws:iam::${accountId || 'SUA_CONTA'}:policy/FinOpsCostExplorerReadOnly`,
    
    getRoleArn: `aws iam get-role --role-name FinOpsReadOnlyRole --query 'Role.Arn' --output text`
  };

  const steps = [
    {
      title: 'Account ID',
      description: 'Identificar sua conta AWS'
    },
    {
      title: 'Criar Role',
      description: 'Configurar IAM Role'
    },
    {
      title: 'Políticas',
      description: 'Anexar permissões'
    },
    {
      title: 'Validar',
      description: 'Confirmar a Role'
    },
    {
      title: 'Conectar',
      description: 'Finalizar setup'
    }
  ];

  return (
    <div className="aws-setup">
      <div className="aws-setup__header">
        <h1>
          <AmazonOutlined />
          Configuração AWS
        </h1>
        <p>
          Guia passo a passo para conectar sua conta AWS com segurança
        </p>
      </div>

      {/* Security Banner */}
      <Alert
        message="🔒 Configuração Segura"
        description="Credenciais temporárias (1h) • Permissões read-only • Auditoria no CloudTrail • External ID para proteção"
        type="success"
        showIcon
        className="aws-setup__security-alert"
      />

      {/* Steps */}
      <Card className="aws-setup__steps-card">
        <Steps current={currentStep} items={steps} />
      </Card>

      {/* Step 0: Account ID */}
      {currentStep === 0 && (
        <Card title="Passo 1: Obter Account ID" className="aws-setup__content-card">
          <div className="space-y-4">
            <Alert
              message="Como encontrar seu Account ID?"
              description="Faça login no AWS Console e clique no seu nome no canto superior direito. O Account ID aparecerá no dropdown."
              type="info"
              showIcon
            />

            <div>
              <label className="block text-sm font-medium mb-2">Nome do cliente *</label>
              <Input
                placeholder="Ex.: Porto Seguro"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                size="large"
              />
              <p className="text-xs text-gray-500 mt-1">Nome comercial exibido nos dashboards e relatórios</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">AWS Account ID *</label>
              <Input
                placeholder="123456789012"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value.replace(/\D/g, '').slice(0, 12))}
                size="large"
                maxLength={12}
              />
              <p className="text-xs text-gray-500 mt-1">12 dígitos numéricos</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Domínio do cliente</label>
              <Input
                placeholder="empresa.com.br"
                value={customerDomain}
                onChange={(e) => setCustomerDomain(e.target.value)}
                size="large"
              />
              <p className="text-xs text-gray-500 mt-1">Usado para carregar o favicon e agrupar conexões</p>
            </div>

            <div className="aws-setup__helper-panel">
              <div className="font-medium mb-2">Via AWS CLI:</div>
              <div className="aws-setup__code-row">
                <code>aws sts get-caller-identity --query Account --output text</code>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard('aws sts get-caller-identity --query Account --output text', 'Comando')}
                  className="aws-setup__copy-button"
                />
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              onClick={() => {
                if (customerName.trim() && accountId.length === 12) {
                  generateRoleArn();
                  setCurrentStep(1);
                } else {
                  message.error('Informe o nome do cliente e um Account ID com 12 dígitos');
                }
              }}
              disabled={!customerName.trim() || accountId.length !== 12}
            >
              Próximo
            </Button>
          </div>
        </Card>
      )}

      {/* Step 1: Create Role */}
      {currentStep === 1 && (
        <Card title="Passo 2: Criar IAM Role" className="aws-setup__content-card">
          <div className="space-y-4">
            <Alert
              message="Vamos criar uma Role com permissões read-only"
              type="info"
              showIcon
            />

            {onboardingError && <Alert message={onboardingError} type="error" showIcon />}
            {onboardingLoading && <Alert message="Gerando Principal e External ID exclusivos..." type="info" showIcon />}

            <Alert
              message="Importante: este JSON é uma Trust Policy"
              description="No Console da AWS, cole este bloco apenas em IAM > Roles > Create role > Custom trust policy ou em Trust relationships > Edit trust policy. Se colar em Create policy/Permissions, a AWS acusa erro porque políticas de permissão não aceitam Principal."
              type="warning"
              showIcon
            />

            <div>
              <div className="aws-setup__section-head">
                <label className="text-sm font-medium">1. Criar trust-policy.json ou colar em Custom trust policy</label>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  disabled={!externalId || !finOpsPrincipalArn}
                  onClick={() => copyToClipboard(trustPolicy, 'Trust Policy')}
                >
                  Copiar
                </Button>
              </div>
              <TextArea
                value={trustPolicy}
                rows={15}
                readOnly
                className="font-mono text-xs"
              />
            </div>

            <div>
              <div className="aws-setup__section-head">
                <label className="text-sm font-medium">2. Executar comando AWS CLI se preferir criar pela linha de comando</label>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(cliCommands.createRole, 'Comando')}
                >
                  Copiar
                </Button>
              </div>
              <div className="aws-setup__code-block">
                <pre>{cliCommands.createRole}</pre>
              </div>
            </div>

            <div className="aws-setup__actions">
              <Button onClick={() => setCurrentStep(0)}>Voltar</Button>
              <Button type="primary" onClick={() => setCurrentStep(2)}>
                Próximo
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 2: Attach Policies */}
      {currentStep === 2 && (
        <Card title="Passo 3: Anexar Políticas" className="aws-setup__content-card">
          <div className="space-y-4">
            <Alert
              message="Anexar políticas AWS gerenciadas (read-only)"
              type="info"
              showIcon
            />

            <Collapse>
              <Panel header="1. Anexar ReadOnlyAccess" key="1">
                <div className="space-y-2">
                  <div className="aws-setup__code-row">
                    <pre>{cliCommands.attachReadOnly}</pre>
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(cliCommands.attachReadOnly, 'Comando')}
                      className="aws-setup__copy-button"
                    />
                  </div>
                </div>
              </Panel>

              <Panel header="2. Criar política Cost Explorer" key="2">
                <div className="space-y-2">
                  <div className="mb-2">
                    <div className="aws-setup__section-head">
                      <span className="text-sm">Criar arquivo cost-explorer-policy.json:</span>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyToClipboard(costExplorerPolicy, 'Política')}
                      >
                        Copiar
                      </Button>
                    </div>
                    <TextArea
                      value={costExplorerPolicy}
                      rows={12}
                      readOnly
                      className="font-mono text-xs"
                    />
                  </div>
                  <div className="aws-setup__code-block">
                    <pre>{cliCommands.createCostPolicy}</pre>
                  </div>
                </div>
              </Panel>

              <Panel header="3. Anexar política Cost Explorer" key="3">
                <div className="aws-setup__code-row">
                  <pre>{cliCommands.attachCostPolicy}</pre>
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(cliCommands.attachCostPolicy, 'Comando')}
                    className="aws-setup__copy-button"
                  />
                </div>
              </Panel>
            </Collapse>

            <div className="aws-setup__actions">
              <Button onClick={() => setCurrentStep(1)}>Voltar</Button>
              <Button type="primary" onClick={() => setCurrentStep(3)}>
                Próximo
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 3: Test */}
      {currentStep === 3 && (
        <Card title="Passo 4: Validar Configuração" className="aws-setup__content-card">
          <div className="space-y-4">
            <Alert
              message="Confirme que a Role foi criada; o acesso será testado com segurança pelo backend no passo seguinte."
              type="info"
              showIcon
            />

            <div>
              <div className="aws-setup__section-head">
                <label className="text-sm font-medium">1. Obter Role ARN</label>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(cliCommands.getRoleArn, 'Comando')}
                >
                  Copiar
                </Button>
              </div>
              <div className="aws-setup__code-block">
                <pre>{cliCommands.getRoleArn}</pre>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Role ARN (resultado do comando acima)</label>
              <Input
                placeholder="arn:aws:iam::123456789012:role/FinOpsReadOnlyRole"
                value={roleArn}
                onChange={(e) => setRoleArn(e.target.value)}
                size="large"
              />
            </div>

            <Alert
              message="O teste AssumeRole precisa partir da plataforma FinOps. Ele será executado ao conectar a conta."
              type="warning"
              showIcon
            />

            <div className="aws-setup__actions">
              <Button onClick={() => setCurrentStep(2)}>Voltar</Button>
              <Button type="primary" onClick={() => setCurrentStep(4)} disabled={!roleArn}>
                Próximo
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 4: Connect */}
      {currentStep === 4 && (
        <Card title="Passo 5: Conectar ao Dashboard" className="aws-setup__content-card">
          <div className="space-y-4">
            <Alert
              message="🎉 Configuração concluída! Agora vamos conectar."
              type="success"
              showIcon
            />

            <div className="aws-setup__summary-grid">
              <div className="aws-setup__summary-item">
                <div className="text-sm font-medium mb-1">Role ARN</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 break-all">{roleArn}</div>
              </div>
              <div className="aws-setup__summary-item">
                <div className="text-sm font-medium mb-1">External ID</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{externalId}</div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Região AWS</label>
              <Select
                value={region}
                onChange={setRegion}
                className="w-full"
                size="large"
              >
                <Option value="us-east-1">US East (N. Virginia)</Option>
                <Option value="us-east-2">US East (Ohio)</Option>
                <Option value="us-west-2">US West (Oregon)</Option>
                <Option value="sa-east-1">South America (São Paulo)</Option>
              </Select>
            </div>

            <div className="aws-setup__callout aws-setup__callout--security">
              <div className="aws-setup__callout-content">
                <SafetyOutlined className="aws-setup__callout-icon" />
                <div>
                  <div className="font-medium mb-2">Segurança Garantida</div>
                  <div className="text-sm space-y-1">
                    <div>✅ Credenciais temporárias (expiram em 1 hora)</div>
                    <div>✅ Permissões read-only (não pode modificar nada)</div>
                    <div>✅ Todas as ações registradas no CloudTrail</div>
                    <div>✅ External ID previne ataques confused deputy</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="aws-setup__callout aws-setup__callout--cost">
              <div className="aws-setup__callout-content">
                <DollarOutlined className="aws-setup__callout-icon" />
                <div>
                  <div className="font-medium mb-2">Custos Estimados</div>
                  <div className="text-sm space-y-1">
                    <div>• Cost Explorer API: ~$0.30-$1.50/mês</div>
                    <div>• Outras APIs: GRÁTIS</div>
                    <div>• Não cria recursos (só lê dados)</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="aws-setup__actions">
              <Button onClick={() => setCurrentStep(3)}>Voltar</Button>
              <Button
                type="primary"
                size="large"
                icon={<CheckCircleOutlined />}
                disabled={!externalId || !finOpsPrincipalArn}
                onClick={() => {
                  // Salvar configuração e redirecionar
                  localStorage.setItem('awsConfig', JSON.stringify({
                    roleArn,
                    region,
                    customerName: customerName.trim(),
                    customerDomain: customerDomain.trim(),
                    method: 'assumeRole'
                  }));
                  message.success('Configuração salva! Redirecionando...');
                  setTimeout(() => {
                    navigate('/connect');
                  }, 1500);
                }}
              >
                Ir para Conectar AWS
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Info Cards */}
      <div className="aws-setup__info-grid">
        <Card size="small" className="aws-setup__info-card">
          <div className="text-center">
            <SafetyOutlined className="aws-setup__info-icon aws-setup__info-icon--security" />
            <div className="font-medium">100% Seguro</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Read-only + Auditoria
            </div>
          </div>
        </Card>
        <Card size="small" className="aws-setup__info-card">
          <div className="text-center">
            <DollarOutlined className="aws-setup__info-icon aws-setup__info-icon--cost" />
            <div className="font-medium">Baixo Custo</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              ~$0.30-$1.50/mês
            </div>
          </div>
        </Card>
        <Card size="small" className="aws-setup__info-card">
          <div className="text-center">
            <CodeOutlined className="aws-setup__info-icon aws-setup__info-icon--setup" />
            <div className="font-medium">Fácil Setup</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              5 minutos via CLI
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AWSSetup;
